// KpiRow — Phase 5a topline endpoint. Port of design/v3/AnalyticsPanels.jsx KpiRow.

import { useTopline, type AnalyticsScope } from '../queries';
import {
  Delta,
  EmptyState,
  ErrorState,
  LoadingState,
} from '../bits';
import { fmtWon, fmtWonShort, fmtNum } from '../../../lib/format';

interface KpiRowProps {
  scope: AnalyticsScope;
  compact?: boolean;
}

export function KpiRow({ scope, compact = false }: KpiRowProps) {
  const q = useTopline(scope);

  if (q.isLoading) return <LoadingState label="KPI 불러오는 중…" />;
  if (q.isError) return <ErrorState message={(q.error as Error).message} />;
  if (!q.data) return <EmptyState />;
  const t = q.data;

  const prevKey = scope.compare === 'yoy' ? 'yoy' : 'prev';
  const compareLabel = scope.compare === 'yoy' ? '전년 동월' : '이전 기간';

  const tiles: Array<{
    label: string;
    value: string;
    unit?: string;
    curr: number;
    prev: number;
    inverse?: boolean;
    accent?: string;
    sub?: string;
  }> = [
    {
      label: '총 매출',
      value: fmtWonShort(t.revenue.value),
      curr: t.revenue.value,
      prev: t.revenue[prevKey],
      accent: 'var(--primary-strong)',
      sub: fmtWon(t.revenue.value),
    },
    {
      label: '주문 수',
      value: fmtNum(t.orders.value),
      unit: '건',
      curr: t.orders.value,
      prev: t.orders[prevKey],
    },
    {
      label: '객단가',
      value: fmtWon(t.aov.value),
      curr: t.aov.value,
      prev: t.aov[prevKey],
    },
    {
      label: '원가율',
      value: t.costRate.value.toFixed(1),
      unit: '%',
      curr: t.costRate.value,
      prev: t.costRate[prevKey],
      inverse: true,
      accent: 'var(--warning)',
      sub: '목표 30%',
    },
    {
      label: '폐기율',
      value: t.wasteRate.value.toFixed(1),
      unit: '%',
      curr: t.wasteRate.value,
      prev: t.wasteRate[prevKey],
      inverse: true,
    },
    {
      label: '예측 오차',
      value: t.forecastErr.value.toFixed(1),
      unit: '%',
      curr: t.forecastErr.value,
      prev: t.forecastErr[prevKey],
      inverse: true,
    },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: compact
          ? 'repeat(2, 1fr)'
          : 'repeat(6, 1fr)',
        gap: compact ? 8 : 12,
      }}
    >
      {tiles.map((tile) => (
        <div
          key={tile.label}
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-weak)',
            borderRadius: 'var(--radius-lg)',
            padding: compact ? '12px 14px' : '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {tile.accent && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 2,
                background: tile.accent,
                opacity: 0.5,
              }}
            />
          )}
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--fg-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {tile.label}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                fontSize: compact ? 20 : 26,
                fontWeight: 700,
                color: 'var(--fg-1)',
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              {tile.value}
            </span>
            {tile.unit && (
              <span
                style={{
                  fontSize: compact ? 12 : 14,
                  fontWeight: 500,
                  color: 'var(--fg-3)',
                }}
              >
                {tile.unit}
              </span>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 2,
              flexWrap: 'wrap',
            }}
          >
            <Delta
              curr={tile.curr}
              prev={tile.prev}
              inverse={tile.inverse}
            />
            <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>
              vs {compareLabel}
            </span>
          </div>
          {tile.sub && !compact && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--fg-3)',
                marginTop: 2,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {tile.sub}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
